import {
  selectAppReconnecting,
  selectDtlsStallRecovery,
  selectIceRelayRequired,
} from 'features/application/applicationSlice';
import { store } from 'store/store';
import { formatErrorForLogging } from 'utils/logger';
import { DtlsStallDetector } from 'utils/webrtc/DtlsStallDetector';
import { classifyHangupReason, isRecoverableHangupReason } from 'utils/webrtc/hangupReason';
import {
  emitDtlsRebuild,
  emitDtlsStall,
  emitJanusHangup,
  HangupRouted,
  IceFeedType,
  RebuildAbandonCause,
  StallEntry,
} from 'utils/webrtc/iceTelemetry';
import { RebuildBudget } from 'utils/webrtc/RebuildBudget';
import { markRelayRequired } from 'utils/webrtc/relayRequiredMemory';
import { SubscribeTokenRefreshError } from 'utils/webrtc/subscribeGrantError';

export const REBUILD_CONNECT_TIMEOUT_MS = 15_000;

export type RebuildTrigger = StallEntry | 'connect_timeout';

export interface DtlsStallHost {
  feedType: IceFeedType;
  /** False once the feed is superseded, torn down or has already reported a failure. */
  canRecover(): boolean;
  /** Whether this feed has a rebuild action at all (ScreensharingFeed: false). */
  canRebuild(): boolean;
  /** Whether this feed's current PC exposes `connectionState` (Firefox < 113 does not):
   *  `pc.connectionState !== undefined`, read from `webrtcStuff.pc` on the receiver side and
   *  `getPeerConnection()` on the publisher side. */
  connectionStateSupported(): boolean;
  /** Build a new relay-only PC. Resolves true when one will be negotiated, false when there
   *  was nothing to re-subscribe, and rejects with SubscribeTokenRefreshError when the subscribe
   *  grant could not be minted. Must gate the wedged handle's callbacks out *before* its first
   *  await — its `oncleanup` otherwise lands mid-rebuild and cancels the connect deadline. */
  rebuild(): Promise<boolean>;
  /** Stop the August ladder's pending restart — an ICE restart cannot help a PC with no DTLS. */
  cancelIceRestart(): void;
  /** The existing modal path: reportMediaFailure('connection_failed', 'failed', { previouslyConnected: true });
   *  `previouslyConnected: true` is asserted rather than measured because other stats aren't
   *  queryable post-hangup. */
  reportFailure(): void;
  /** Extra cleanup after a rebuild threw, run after reportFailure (receiver: feed.cleanupFeed()). */
  onRebuildThrew?(): void;
}

interface PendingRebuild {
  attempt: number;
  startedAt: number;
  connectTimer: number;
}

// An ICE restart continues the same DTLS association (RFC 9429 §5.11), so a PC that never
// completed DTLS needs a new PC entirely — relay-only, since the direct path already failed.
export class DtlsStallRecovery {
  private readonly detector: DtlsStallDetector;

  private readonly budget = new RebuildBudget();

  private pending: PendingRebuild | null = null;

  // Latches true→false on the first stall emitted per PC generation, so a Sentry query can
  // filter prevalence without needing a PC id.
  private isFirstStallForPc = true;

  constructor(private readonly host: DtlsStallHost) {
    this.detector = new DtlsStallDetector(() => {
      this.onStall('detector');
    });
  }

  get dtlsEverConnected() {
    return this.detector.dtlsEverConnected;
  }

  // Per PC: reset by `onNewPeerConnection`, untouched by an in-place ICE restart.
  get iceEverConnected() {
    return this.detector.iceConnectMs() !== null;
  }

  onIceConnected() {
    this.detector.onIceConnected(this.host.connectionStateSupported());
  }

  onIceDisconnected() {
    return this.detector.iceConnectedWithoutDtls && this.onStall('ice_disconnected');
  }

  onConnectionState(state: RTCPeerConnectionState) {
    const verdict = this.detector.onConnectionState(state);

    if (verdict === 'established') {
      this.onEstablished();
    } else if (verdict === 'failed') {
      this.onStall('dtls_failed');
    }
  }

  onWebRTCUp() {
    if (this.detector.dtlsEverConnected) {
      return;
    }

    this.detector.markEstablished();
    this.onEstablished();
  }

  onStall(entry: StallEntry): boolean {
    if (!this.host.canRecover()) {
      return false;
    }

    const state = store.getState();
    const recoveryEnabled = selectDtlsStallRecovery(state) && this.host.canRebuild();
    const iceConnectMs = this.detector.iceConnectMs();
    const firstForPc = this.isFirstStallForPc;
    this.isFirstStallForPc = false;

    emitDtlsStall({
      feedType: this.host.feedType,
      entry,
      iceConnectMs,
      connectedLifetimeMs: entry === 'ice_disconnected' ? iceConnectMs : null,
      relayRequiredBefore: selectIceRelayRequired(state),
      recoveryEnabled,
      firstForPc,
    });

    if (!recoveryEnabled) {
      return false;
    }

    this.host.cancelIceRestart();
    this.detector.dispose();
    markRelayRequired();
    this.rebuild(entry);

    return true;
  }

  // Janus's hangup reason is the only server-side verdict on a PC that never completed DTLS.
  routeHangup(
    rawReason: string | undefined,
    meta: { iceRestartAttempt: boolean; mediaFailureReported: boolean }
  ): void {
    const reason = classifyHangupReason(rawReason);
    const { dtlsEverConnected } = this;
    const actionable = isRecoverableHangupReason(reason) || meta.iceRestartAttempt;
    let routed: HangupRouted = 'ignored';

    if (actionable && !meta.mediaFailureReported) {
      if (!dtlsEverConnected && this.onStall('janus_hangup')) {
        routed = 'rebuild';
      } else {
        this.host.reportFailure();
        routed = 'modal';
      }
    }

    emitJanusHangup({
      feedType: this.host.feedType,
      reason,
      rawReason: rawReason ?? null,
      dtlsEverConnected,
      routed,
    });
  }

  onNewPeerConnection() {
    this.isFirstStallForPc = true;
    this.detector.resetForNewPc();
  }

  onPeerConnectionGone() {
    this.detector.dispose();
  }

  // Without an outcome here, a rebuild dropped by a teardown reads as a stall that was never handled.
  // A reconnect tears down every feed, so it outranks whatever the caller saw.
  abandonRebuild(cause: Exclude<RebuildAbandonCause, 'reconnect'>) {
    if (!this.pending) {
      return;
    }

    const abandonCause = selectAppReconnecting(store.getState()) ? 'reconnect' : cause;
    this.settlePending('abandoned', abandonCause);
  }

  private settlePending(outcome: 'abandoned' | 'janus_hangup', abandonCause?: RebuildAbandonCause) {
    const { pending } = this;
    if (!pending) {
      return;
    }

    this.clearPending();
    emitDtlsRebuild({
      feedType: this.host.feedType,
      outcome,
      attempt: pending.attempt,
      rebuildMs: Date.now() - pending.startedAt,
      policy: 'relay',
      error: null,
      ...(abandonCause && { abandonCause }),
    });
  }

  private clearPending() {
    if (this.pending) {
      window.clearTimeout(this.pending.connectTimer);
      this.pending = null;
    }
  }

  private async rebuild(trigger: RebuildTrigger) {
    if (this.pending) {
      // A second signal (e.g. 'failed' after 'ice_disconnected') can describe the same death —
      // one rebuild is already in flight and already covers every outcome, so let it run.
      if (trigger !== 'janus_hangup') {
        return;
      }

      // Janus hung up the attempt we were waiting on; past the handle gate it is never a duplicate.
      this.settlePending('janus_hangup');
    }

    const { allowed, attempt } = this.budget.consume();
    const base = { feedType: this.host.feedType, attempt, policy: 'relay' as const };

    if (!allowed) {
      emitDtlsRebuild({ ...base, outcome: 'budget_exhausted', rebuildMs: null, error: null });
      this.host.reportFailure();
      return;
    }

    const startedAt = Date.now();
    this.pending = {
      attempt,
      startedAt,
      connectTimer: window.setTimeout(this.onConnectTimeout, REBUILD_CONNECT_TIMEOUT_MS),
    };

    try {
      const negotiating = await this.host.rebuild();

      if (!negotiating) {
        this.clearPending();
        emitDtlsRebuild({
          ...base,
          outcome: 'no_op',
          rebuildMs: Date.now() - startedAt,
          error: null,
        });
      }
    } catch (error) {
      this.clearPending();
      emitDtlsRebuild({
        ...base,
        outcome: error instanceof SubscribeTokenRefreshError ? 'token_refresh_failed' : 'threw',
        rebuildMs: Date.now() - startedAt,
        error: formatErrorForLogging(error).message,
      });

      this.host.reportFailure();
      this.host.onRebuildThrew?.();
    }
  }

  private onConnectTimeout = () => {
    const { pending } = this;
    if (!pending) {
      return;
    }

    this.pending = null;
    emitDtlsRebuild({
      feedType: this.host.feedType,
      outcome: 'connect_timeout',
      attempt: pending.attempt,
      rebuildMs: Date.now() - pending.startedAt,
      policy: 'relay',
      error: null,
    });

    if (!this.host.canRecover()) {
      return;
    }

    this.detector.dispose();
    this.rebuild('connect_timeout');
  };

  private onEstablished() {
    const { pending } = this;
    if (!pending) {
      return;
    }

    this.clearPending();
    emitDtlsRebuild({
      feedType: this.host.feedType,
      outcome: 'connected',
      attempt: pending.attempt,
      rebuildMs: Date.now() - pending.startedAt,
      policy: 'relay',
      error: null,
    });
  }
}
